import { AgendaTask, AgendaTaskList } from '../components/AgendaPage';

export type SortMode = 'balance' | 'work-first' | 'random' | 'energy' | 'manual';

export interface TaskWithMeta {
  task: AgendaTask;
  list: AgendaTaskList;
  event?: any;
}

// Get smart default based on time of day
export const getSmartSortMode = (): SortMode => {
  const hour = new Date().getHours();
  
  if (hour >= 8 && hour < 12) {
    return 'work-first'; // Morning: focus on work
  } else if (hour >= 12 && hour < 17) {
    return 'balance'; // Afternoon: balanced approach
  } else {
    return 'energy'; // Evening: filter work, show lighter tasks
  }
};

// Balance mode: group by category
export const sortByBalance = (tasks: TaskWithMeta[]): TaskWithMeta[] => {
  const order = ['work', 'self', 'family', 'friends', 'activity'];
  
  return tasks.sort((a, b) => {
    const catA = a.list.balanceCategory || 'none';
    const catB = b.list.balanceCategory || 'none';
    
    const indexA = order.indexOf(catA);
    const indexB = order.indexOf(catB);
    
    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    
    return indexA - indexB;
  });
};

// Work-first mode: prioritize work, then everything else
export const sortByWorkFirst = (tasks: TaskWithMeta[]): TaskWithMeta[] => {
  return tasks.sort((a, b) => {
    const isWorkA = a.list.balanceCategory === 'work' ? 0 : 1;
    const isWorkB = b.list.balanceCategory === 'work' ? 0 : 1;
    return isWorkA - isWorkB;
  });
};

// Random mode: shuffle tasks
export const shuffleTasks = (tasks: TaskWithMeta[]): TaskWithMeta[] => {
  const shuffled = [...tasks];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Energy mode: filter work in evening, show lighter tasks first
export const sortByEnergy = (tasks: TaskWithMeta[]): TaskWithMeta[] => {
  const hour = new Date().getHours();
  const isEvening = hour >= 18;
  
  let filtered = tasks;
  
  if (isEvening) {
    // Filter out work tasks
    filtered = tasks.filter(t => t.list.balanceCategory !== 'work');
  }
  
  // Sort lighter tasks first (personal, activity)
  return filtered.sort((a, b) => {
    const lightCategories = ['activity', 'personal', 'self'];
    const isLightA = lightCategories.includes(a.list.balanceCategory || '') ? 0 : 1;
    const isLightB = lightCategories.includes(b.list.balanceCategory || '') ? 0 : 1;
    return isLightA - isLightB;
  });
};

// Main sort function
export const sortTasks = (
  tasks: TaskWithMeta[],
  mode: SortMode,
  customOrder?: string[]
): TaskWithMeta[] => {
  if (mode === 'manual' && customOrder) {
    // Sort by custom order
    return tasks.sort((a, b) => {
      const indexA = customOrder.indexOf(a.task.id);
      const indexB = customOrder.indexOf(b.task.id);
      
      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      
      return indexA - indexB;
    });
  }
  
  switch (mode) {
    case 'balance':
      return sortByBalance(tasks);
    case 'work-first':
      return sortByWorkFirst(tasks);
    case 'random':
      return shuffleTasks(tasks);
    case 'energy':
      return sortByEnergy(tasks);
    default:
      return tasks;
  }
};
